import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler, successHandler } from "../../../helpers/handlers";

const initialState = {
  profile: null,
  matches: [],
  gate: null,
  quota: null,
  conversationId: null,
  chatMessages: [],
  pageLoading: false,
  matchLoading: false,
  chatLoading: false,
  error: null,
};

const slice = createSlice({
  name: "advisor",
  initialState,
  reducers: {
    clearMatches: (state) => {
      state.matches = [];
      state.gate = null;
    },
    clearChat: (state) => {
      state.conversationId = null;
      state.chatMessages = [];
    },
    appendLocalChat: (state, action) => {
      state.chatMessages.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdvisorProfile.pending, (state) => {
        state.pageLoading = true;
        state.error = null;
      })
      .addCase(fetchAdvisorProfile.fulfilled, (state, action) => {
        state.pageLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchAdvisorProfile.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      })

      .addCase(saveAdvisorProfile.pending, (state) => {
        state.matchLoading = true;
        state.error = null;
      })
      .addCase(saveAdvisorProfile.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(saveAdvisorProfile.rejected, (state, action) => {
        state.matchLoading = false;
        state.error = action.payload;
      })

      .addCase(runAdvisorMatch.pending, (state) => {
        state.matchLoading = true;
        state.error = null;
      })
      .addCase(runAdvisorMatch.fulfilled, (state, action) => {
        state.matchLoading = false;
        state.matches = action.payload.matches || [];
        state.gate = action.payload.gate || null;
        state.quota = action.payload.quota || state.quota;
      })
      .addCase(runAdvisorMatch.rejected, (state, action) => {
        state.matchLoading = false;
        state.error = action.payload;
      })

      .addCase(runAdvisorChat.pending, (state) => {
        state.chatLoading = true;
        state.error = null;
      })
      .addCase(runAdvisorChat.fulfilled, (state, action) => {
        state.chatLoading = false;
        state.conversationId = action.payload.conversationId;
        state.chatMessages.push({
          role: "assistant",
          content: action.payload.reply,
          citations: action.payload.citations || [],
        });
        if (action.payload.quota) {
          state.quota = {
            ...(state.quota || {}),
            ...action.payload.quota,
          };
        }
      })
      .addCase(runAdvisorChat.rejected, (state, action) => {
        state.chatLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchAdvisorQuota.fulfilled, (state, action) => {
        state.quota = action.payload;
      });
  },
});

export const { clearMatches, clearChat, appendLocalChat } = slice.actions;

export const fetchAdvisorProfile = createAsyncThunk(
  "advisor/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/user/advisor/profile");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export const saveAdvisorProfile = createAsyncThunk(
  "advisor/saveProfile",
  async (profile, { rejectWithValue }) => {
    try {
      const response = await api.post("/user/advisor/profile", profile);
      successHandler("Profile saved");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export const runAdvisorMatch = createAsyncThunk(
  "advisor/runMatch",
  async ({ refinements = [] } = {}, { rejectWithValue }) => {
    try {
      const response = await api.post("/user/advisor/match", {
        useStoredProfile: true,
        refinements,
      });
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      const payload = error.response?.data;
      return rejectWithValue(
        payload?.errors?.global ||
          payload?.message ||
          error.message
      );
    }
  }
);

export const runAdvisorChat = createAsyncThunk(
  "advisor/runChat",
  async ({ message, conversationId, lastMatchIds }, { rejectWithValue }) => {
    try {
      const response = await api.post("/user/advisor/chat", {
        message,
        conversationId: conversationId || undefined,
        lastMatchIds:
          Array.isArray(lastMatchIds) && lastMatchIds.length
            ? lastMatchIds
            : undefined,
      });
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export const fetchAdvisorQuota = createAsyncThunk(
  "advisor/fetchQuota",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/user/advisor/quota");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export default slice.reducer;
